import React from "react";
import { CheckCircle, Camera, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { REPAIR_CATEGORIES } from "@/utils/constants";

// Step Progress Indicator
export const StepProgress = ({ currentStep, totalSteps = 4 }) => {
  const steps = Array.from({ length: totalSteps }, (_, i) => i + 1);
  
  return (
    <div className="flex items-center justify-center mb-2">
      {steps.map((s) => (
        <div key={s} className="flex items-center">
          <div 
            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
              currentStep === s ? 'bg-blue-600 text-white' : 
              currentStep > s ? 'bg-green-500 text-white' : 
              'bg-slate-200 text-slate-600'
            }`}
          >
            {currentStep > s ? <CheckCircle className="w-3.5 h-3.5" /> : s}
          </div>
          {s < totalSteps && (
            <div className={`w-6 sm:w-12 h-0.5 mx-1 rounded ${currentStep > s ? 'bg-green-500' : 'bg-slate-200'}`} />
          )}
        </div>
      ))}
    </div>
  );
};

// Step 1: Select Unit
export const SelectUnitStep = ({ 
  vehicles, 
  selectedVehicle, 
  onSelectVehicle,
  mileage, 
  onMileageChange,
  hours,
  onHoursChange,
  priority,
  onPriorityChange
}) => {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="vehicle-select">Select Unit *</Label>
        <Select 
          value={selectedVehicle?.id || ""} 
          onValueChange={(value) => {
            const vehicle = vehicles.find(v => v.id === value);
            onSelectVehicle(vehicle);
          }}
        >
          <SelectTrigger data-testid="select-vehicle">
            <SelectValue placeholder="Select a vehicle..." />
          </SelectTrigger>
          <SelectContent>
            {vehicles.map((v) => (
              <SelectItem key={v.id} value={v.id}>
                Unit {v.unit_number} - {v.year} {v.make} {v.model}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedVehicle && (
        <div className="bg-slate-50 rounded-lg p-3 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div><span className="text-slate-500">Type:</span> <span className="font-medium capitalize">{selectedVehicle.vehicle_type.replace('_', ' ')}</span></div>
            <div><span className="text-slate-500">VIN:</span> <span className="font-medium">{selectedVehicle.vin}</span></div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="mileage">Mileage (optional)</Label>
          <Input
            id="mileage"
            data-testid="input-mileage"
            value={mileage}
            onChange={onMileageChange}
            placeholder="e.g., 150,000"
          />
        </div>
        <div>
          <Label htmlFor="hours">Hours (optional)</Label>
          <Input
            id="hours"
            data-testid="input-hours"
            value={hours}
            onChange={onHoursChange}
            placeholder="e.g., 5,000"
          />
        </div>
      </div>

      {/* Priority Selection */}
      <div>
        <Label>Priority Level *</Label>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {[
            { value: 'low', label: 'Low', color: 'green', desc: 'When convenient' },
            { value: 'medium', label: 'Medium', color: 'yellow', desc: 'Within a few days' },
            { value: 'high', label: 'High', color: 'red', desc: 'ASAP' }
          ].map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => onPriorityChange(p.value)}
              className={`p-2 rounded-lg border-2 text-center transition-all ${
                priority === p.value
                  ? `border-${p.color}-500 bg-${p.color}-50 text-${p.color}-700`
                  : "border-slate-200 hover:border-slate-300"
              }`}
              style={priority === p.value ? {
                borderColor: p.color === 'green' ? '#22c55e' : p.color === 'yellow' ? '#eab308' : '#ef4444',
                backgroundColor: p.color === 'green' ? '#f0fdf4' : p.color === 'yellow' ? '#fefce8' : '#fef2f2'
              } : {}}
              data-testid={`priority-${p.value}`}
            >
              <span className="font-medium text-sm">{p.label}</span>
              <p className="text-xs text-slate-500">{p.desc}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Step 2: Select Category
export const SelectCategoryStep = ({ selectedCategory, onSelectCategory }) => {
  return (
    <div className="space-y-4">
      <Label>Select issue category *</Label>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[300px] overflow-y-auto">
        {REPAIR_CATEGORIES.map((category) => (
          <button
            key={category}
            onClick={() => onSelectCategory(category)}
            className={`p-3 rounded-lg border-2 text-left transition-all text-sm ${
              selectedCategory === category 
                ? 'border-blue-600 bg-blue-50 text-blue-900' 
                : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50/50'
            }`}
            data-testid={`category-${category.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {category}
          </button>
        ))}
      </div>
    </div>
  );
};

// Step 3: Describe Issue
export const DescribeIssueStep = ({ notes, onNotesChange }) => {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="notes">Describe the issue *</Label>
        <Textarea
          id="notes"
          data-testid="textarea-notes"
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="Please describe the issue in detail. Include any symptoms, when the problem started, and any relevant information..."
          className="min-h-[150px] resize-none"
          rows={6}
        />
        <p className="text-xs text-slate-500 mt-2">
          Be specific about the problem to help technicians diagnose and fix the issue faster.
        </p>
      </div>
    </div>
  );
};

// Step 4: Upload Images
export const UploadImagesStep = ({ images, onImageUpload, onRemoveImage, onImageClick }) => {
  return (
    <div className="space-y-4">
      <div>
        <Label>Attach Photos (optional)</Label>
        <p className="text-xs text-slate-500 mt-1 mb-3">
          Photos help technicians understand the issue better
        </p>
        
        {/* Image Preview Grid */}
        {images.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-3">
            {images.map((img, idx) => (
              <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group">
                <img 
                  src={img} 
                  alt={`Issue ${idx + 1}`} 
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => onImageClick(img)}
                />
                <button
                  type="button"
                  onClick={() => onRemoveImage(idx)}
                  className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        
        {/* Upload Button */}
        <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
          <Camera className="w-8 h-8 text-slate-400 mb-2" />
          <span className="text-sm text-slate-600">
            {images.length > 0 ? "Add More Photos" : "Tap to add photos"}
          </span>
          <span className="text-xs text-slate-400 mt-1">Max 5MB per image</span>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={onImageUpload}
            className="hidden"
            data-testid="input-images"
          />
        </label>
      </div>
    </div>
  );
};

// Complete Service Request Dialog
export const CompleteRequestDialog = ({
  isOpen,
  onClose,
  request,
  mileage,
  onMileageChange,
  notes,
  onNotesChange,
  images,
  onImageUpload,
  onRemoveImage,
  onImageClick,
  onConfirm,
  isUpdating
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none sm:max-w-xl sm:max-h-[90vh] sm:h-auto sm:m-auto sm:rounded-lg !left-0 !top-0 !translate-x-0 !translate-y-0 sm:!left-[50%] sm:!top-[50%] sm:!translate-x-[-50%] sm:!translate-y-[-50%] flex flex-col">
        <DialogHeader className="flex-shrink-0 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-green-600 text-xl">
            <CheckCircle className="w-6 h-6" />
            Complete Service Request
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto py-4 sm:py-6 space-y-5 px-1">
          {/* Request Details Card */}
          {request && (
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 text-sm">Unit</span>
                  <span className="font-bold text-lg text-slate-900">{request.unit_number}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 text-sm">Category</span>
                  <span className="font-semibold text-slate-800 capitalize">{request.category}</span>
                </div>
                {request.notes && (
                  <div className="pt-2 border-t border-slate-200">
                    <span className="text-slate-500 text-sm block mb-1">Original Request</span>
                    <p className="text-slate-700 text-sm">{request.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Odometer Input */}
          <div className="space-y-2">
            <Label htmlFor="complete-mileage" className="text-base font-semibold">
              Confirm Odometer Reading *
            </Label>
            <Input
              id="complete-mileage"
              data-testid="input-complete-mileage"
              value={mileage}
              onChange={onMileageChange}
              placeholder="e.g., 150,000"
              className="text-lg h-12 text-center font-mono"
              inputMode="numeric"
            />
          </div>
          
          {/* Completion Notes - Required */}
          <div className="space-y-2">
            <Label htmlFor="complete-notes" className="text-base font-semibold">
              Completion Notes *
            </Label>
            <p className="text-sm text-slate-500">
              Describe the work completed, parts replaced, or any observations
            </p>
            <Textarea
              id="complete-notes"
              data-testid="input-complete-notes"
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder="e.g., Replaced brake pads on all wheels. Inspected rotors - still within spec. Test drove vehicle, brakes functioning properly."
              className="min-h-[100px] resize-none"
              rows={4}
            />
          </div>
          
          {/* Photo Upload */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">
              Photos (Optional)
            </Label>
            <p className="text-sm text-slate-500">
              Add photos of completed work, parts replaced, or repair documentation
            </p>
            
            {/* Image Preview Grid */}
            {images.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                {images.map((img, idx) => (
                  <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group">
                    <img 
                      src={img} 
                      alt={`Completion ${idx + 1}`} 
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => onImageClick(img)}
                    />
                    <button
                      type="button"
                      onClick={() => onRemoveImage(idx)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
                      title="Remove photo"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Upload Button */}
            <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-green-400 hover:bg-green-50 transition-colors">
              <Camera className="w-5 h-5 text-slate-400" />
              <span className="text-slate-600 text-sm">
                {images.length > 0 ? "Add More Photos" : "Add Photos"}
              </span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={onImageUpload}
                className="hidden"
                data-testid="input-complete-images"
              />
            </label>
          </div>
        </div>
        
        {/* Action Buttons - Fixed at Bottom */}
        <div className="flex-shrink-0 pt-4 border-t bg-white">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              variant="outline" 
              onClick={onClose}
              className="h-12 sm:h-10 order-2 sm:order-1 sm:flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={onConfirm} 
              disabled={!mileage || !notes.trim() || isUpdating}
              className="bg-green-600 hover:bg-green-700 h-12 sm:h-10 order-1 sm:order-2 sm:flex-1"
              data-testid="btn-confirm-complete"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              {isUpdating ? "Completing..." : "Complete Request"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
